import { ImportTableColumn, DefaultImportTableColumns, ColumnSetting, hardcodedColumns } from "../types";
import { columnDisplayNames } from "@domain/CRM/job";

/**
 * Initialize column settings with default values from DefaultImportTableColumns
 */
export function getDefaultColumnSettings(): Record<string, ColumnSetting> {
	const settings: Record<string, ColumnSetting> = {};

	Object.keys(columnDisplayNames).forEach((key) => {
		const defaultSetting = DefaultImportTableColumns[key as ImportTableColumn];
		settings[key] = defaultSetting
			? { visible: defaultSetting.visible, required: defaultSetting.required }
			: { visible: false, required: false };
	});

	return settings;
}

/**
 * Apply hardcoded field rules to ensure always-visible and always-required fields are set correctly
 */
export function applyHardcodedRules(settings: Record<string, ColumnSetting>): Record<string, ColumnSetting> {
	const updatedSettings = { ...settings };

	Object.entries(hardcodedColumns).forEach(([column, config]) => {
		if (!updatedSettings[column]) {
			updatedSettings[column] = { visible: false, required: false };
		}
		if (config.alwaysVisible) {
			updatedSettings[column].visible = true;
		}
		if (config.alwaysRequired) {
			updatedSettings[column].required = true;
		}
	});

	return updatedSettings;
}

/**
 * Validate whether a column change is allowed based on hardcoded rules and dependencies
 * @returns true if the change is allowed, false otherwise
 */
export function validateColumnChange(
	column: ImportTableColumn,
	field: "visible" | "required",
	value: boolean,
	currentSettings: Record<string, ColumnSetting>,
): boolean {
	const hardcoded = hardcodedColumns[column];

	// Prevent changes to hardcoded fields
	if (
		hardcoded &&
		((field === "visible" && hardcoded.alwaysVisible) || (field === "required" && hardcoded.alwaysRequired))
	) {
		return false;
	}

	// If trying to uncheck visible while required is checked, prevent it
	if (field === "visible" && value === false && currentSettings[column]?.required) {
		return false;
	}

	return true;
}
